// Main scoring interface for judges to score projects using active rubrics
// This component displays rubric questions and allows judges to select points for each

import { useState, useMemo, useEffect } from 'react';
import {
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import {
  getActiveRubricsByFair,
  saveRubricScore,
  getJudgeRubricScore,
} from '../../../services/rubric.service';
import type {
  Rubric,
  RubricQuestion,
  ProjectRubricScore,
} from '../../../types/rubric.types';

// TypeScript interface defining the props this component expects
type Props = {
  projectId: string; // ID of the project being scored
  judgeId: string; // ID of the judge doing the scoring
  fairId: string; // ID of the fair to get rubrics for
  onSubmit: () => void; // Callback function when judge submits their score
};

/**
 * RubricScoringPanel - The main scoring interface for judges
 * Displays rubric dropdown and questions with point selectors
 * Calculates and displays running total score
 */
export default function RubricScoringPanel({
  projectId,
  judgeId,
  fairId,
  onSubmit,
}: Props) {
  // State to store available rubrics for this fair
  const [availableRubrics, setAvailableRubrics] = useState<Rubric[]>([]);

  // State to store the selected rubric
  const [selectedRubricId, setSelectedRubricId] = useState<string>('');
  const [rubric, setRubric] = useState<Rubric | null>(null);

  // State to store judge's scores for each question
  // Key is question index, value is points awarded
  const [scores, setScores] = useState<Record<number, number>>({});

  // State to track if there's an existing score for this judge/project
  const [existingScore, setExistingScore] = useState<ProjectRubricScore | null>(
    null
  );

  // State for loading and error handling
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Load the active rubrics for this fair when component mounts
  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        // Load active rubrics for this fair
        const rubrics = await getActiveRubricsByFair(fairId);
        setAvailableRubrics(rubrics);

        // Auto-select if only one rubric
        if (rubrics.length === 1) {
          setSelectedRubricId(rubrics[0].id);
          setRubric(rubrics[0]);
        }

        // Check if this judge has already scored this project
        if (projectId && judgeId) {
          const existing = await getJudgeRubricScore(projectId, judgeId);
          setExistingScore(existing);

          // If there's an existing score, populate the form
          if (existing) {
            setSelectedRubricId(existing.rubricId);
            const selectedRubric = rubrics.find(
              (r) => r.id === existing.rubricId
            );
            if (selectedRubric) {
              setRubric(selectedRubric);
            }

            const existingScores: Record<number, number> = {};
            existing.scores.forEach((score, index) => {
              existingScores[index] = score.pointsAwarded;
            });
            setScores(existingScores);
          }
        }
      } catch (error) {
        console.error('Error loading rubric data:', error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [projectId, judgeId, fairId]); // Re-run when project, judge, or fair changes

  // Handle rubric selection from dropdown
  const handleRubricChange = (rubricId: string) => {
    setSelectedRubricId(rubricId);
    const selected = availableRubrics.find((r) => r.id === rubricId);
    setRubric(selected || null);
    setScores({}); // Reset scores when changing rubric
  };

  // Calculate total score using useMemo for performance
  // This recalculates whenever rubric or scores change
  const total = useMemo(() => {
    if (!rubric) return 0;
    // Sum up all the scores, using 0 if no score selected for a question
    return rubric.questions.reduce((sum, q, i) => sum + (scores[i] ?? 0), 0);
  }, [rubric, scores]);

  // Handle saving the rubric score
  const handleSubmit = async () => {
    if (!rubric || !projectId || !judgeId) return;

    try {
      setIsSaving(true);

      // Convert scores to the format expected by the database
      const rubricScores = rubric.questions.map((question, index) => ({
        questionId: question.id,
        pointsAwarded: scores[index] || 0,
      }));

      // Save the score to the database
      await saveRubricScore(projectId, judgeId, rubric.id, rubricScores, total);

      console.log('Rubric score saved successfully');
      onSubmit(); // Notify parent component
    } catch (error) {
      console.error('Error saving rubric score:', error);
      alert('Failed to save score. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Show loading state while rubrics are being fetched
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state if no rubrics are available
  if (availableRubrics.length === 0) {
    return (
      <Alert severity="warning">
        No active rubrics available for this fair. Please contact an
        administrator.
      </Alert>
    );
  }

  return (
    <Card className="p-4 space-y-4">
      {/* Rubric Selection Dropdown */}
      {availableRubrics.length > 1 && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select Rubric</InputLabel>
          <Select
            value={selectedRubricId}
            label="Select Rubric"
            onChange={(e) => handleRubricChange(e.target.value)}
            disabled={!!existingScore}
          >
            {availableRubrics.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name} ({r.totalPoints} points)
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {!rubric ? (
        <Alert severity="info">Please select a rubric to begin scoring</Alert>
      ) : (
        <>
          {/* Rubric title */}
          <div className="text-lg font-semibold">{rubric.name}</div>

          {/* List of questions with scoring dropdowns */}
          <div className="space-y-3">
            {rubric.questions.map((q: RubricQuestion, i: number) => (
              <div key={i} className="flex items-center justify-between gap-4">
                {/* Question text and max points info */}
                <div className="flex-1">
                  <div className="font-medium whitespace-pre-wrap break-words">
                    {q.question}
                  </div>
                  <div className="text-sm text-gray-500">
                    Max: {q.maxPoints} points
                  </div>
                </div>

                {/* Dropdown for selecting points (0 to maxPoints) */}
                <select
                  className="rounded border px-3 py-2"
                  value={scores[i] ?? ''} // Show current selection or empty
                  onChange={(e) =>
                    setScores({ ...scores, [i]: Number(e.target.value) })
                  }
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {/* Generate options from 0 to maxPoints */}
                  {Array.from({ length: q.maxPoints + 1 }, (_, v) => v).map(
                    (v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    )
                  )}
                </select>
              </div>
            ))}
          </div>

          {/* Total score display and submit button */}
          <div className="flex items-center justify-between">
            <div className="font-semibold">
              Total: {total} / {rubric.totalPoints}
            </div>
            <Button
              onClick={handleSubmit}
              // Disable submit if any question hasn't been scored or if currently saving
              disabled={
                rubric.questions.some((_, i) => scores[i] == null) || isSaving
              }
            >
              {isSaving
                ? 'Saving...'
                : existingScore
                  ? 'Update Score'
                  : 'Submit Score'}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
